import { Component, OnInit } from '@angular/core';
import { NotificationsService } from 'angular2-notifications';
import { UserService } from '../services/user.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';
import { ColDef, GridOptions, GridApi, ColumnApi } from "ag-grid-community";

@Component({
  selector: 'app-s3-reports',
  templateUrl: './s3-reports.component.html',
  styleUrls: ['./s3-reports.component.css']
})
export class S3ReportsComponent implements OnInit {
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100
  };
  isLoading: boolean = true;

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.initializeGrid();
  }

  ngOnInit() {
    this.loadReports();
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: 'Filename',
        field: 'filename',
        flex: 2,
        minWidth: 200
      },
      {
        headerName: 'Path/Folder',
        field: 'key',
        valueFormatter: (params) => {
          if (!params.value) return '';
          const parts = params.value.split('/');
          if (parts.length > 1) {
            return parts.slice(0, -1).join('/') + '/';
          }
          return 'Root';
        },
        flex: 2,
        minWidth: 150
      },
      {
        headerName: 'Size',
        field: 'size',
        valueFormatter: (params) => {
          return this.formatFileSize(params.value);
        },
        flex: 1,
        minWidth: 100
      },
      {
        headerName: 'Last Modified',
        field: 'lastModified',
        valueFormatter: (params) => {
          return this.formatDate(params.value);
        },
        flex: 2,
        minWidth: 180
      },
      {
        headerName: 'Actions',
        field: 'action',
        cellRenderer: (params) => {
          const report = params.data;
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.justifyContent = 'center';
          container.style.height = '100%';
          
          const downloadBtn = document.createElement('button');
          downloadBtn.type = 'button';
          downloadBtn.className = 'btn btn-sm btn-primary';
          downloadBtn.title = 'Download';
          downloadBtn.style.display = 'flex';
          downloadBtn.style.alignItems = 'center';
          downloadBtn.style.justifyContent = 'center';
          downloadBtn.style.padding = '4px 12px';
          downloadBtn.innerHTML = '<i class="fa fa-download"></i> Download';
          downloadBtn.onclick = (e) => {
            e.stopPropagation();
            this.downloadReport(report);
          };
          container.appendChild(downloadBtn);
          
          return container;
        },
        sortable: false,
        filter: false,
        flex: 1,
        minWidth: 120
      }
    ];

    this.gridOptions = {
      enableCellTextSelection: true,
      ensureDomOrder: true,
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColDef,
      rowData: this.rowData,
      pagination: true,
      paginationPageSize: 20,
      suppressHorizontalScroll: true,
      enableRangeSelection: true,
      animateRows: true,
      rowHeight: 40,
      headerHeight: 40,
      domLayout: 'normal',
      suppressScrollOnNewData: true
    };
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
  }

  loadReports() {
    this.isLoading = true;
    this.spinnerService.show();
    
    console.log("Loading S3 reports...");
    
    this.userService.listAllS3Reports().subscribe(
      (response: any) => {
        console.log("S3 Reports Response:", response);
        this.spinnerService.hide();
        this.isLoading = false;
        
        if (response && response.status && response.success && response.data) {
          const reports = response.data;
          console.log("Reports count:", reports ? reports.length : 0);
          
          // Sort reports by lastModified descending (newest first)
          if (reports && reports.length > 0) {
            reports.sort((a, b) => {
              return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
            });
          }
          
          this.rowData = reports || [];
          if (this.gridApi) {
            this.gridApi.setRowData(this.rowData);
            this.gridApi.sizeColumnsToFit();
          }
          
          if (!reports || reports.length === 0) {
            this.notifSer.info("Info", "No reports found", {
              timeOut: 3000,
              showProgressBar: true,
              pauseOnHover: false,
              clickToClose: true,
              maxLength: 50,
            });
          }
        } else {
          console.warn("Unexpected response format:", response);
          this.rowData = [];
          if (this.gridApi) {
            this.gridApi.setRowData(this.rowData);
          }
          this.notifSer.alert("Warning", "No reports found or invalid response format", {
            timeOut: 3000,
            showProgressBar: true,
            pauseOnHover: false,
            clickToClose: true,
            maxLength: 50,
          });
        }
      },
      (error) => {
        console.error("Error loading S3 reports:", error);
        this.spinnerService.hide();
        this.isLoading = false;
        this.rowData = [];
        if (this.gridApi) {
          this.gridApi.setRowData(this.rowData);
        }
        
        const errorMessage = (error.error && error.error.message) || 
                            (error.message) || 
                            "Failed to load S3 reports. Please try again.";
        
        this.notifSer.error("Error", errorMessage, {
          timeOut: 5000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 100,
        });
      }
    );
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  formatDate(date: string | Date): string {
    if (!date) return '';
    return moment(date).format('MMM DD, YYYY hh:mm:ss A');
  }

  downloadReport(report: any) {
    if (!report || !report.key) {
      this.notifSer.error("Error", "Invalid report data. Key is missing.", {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: true,
        maxLength: 50,
      });
      return;
    }

    this.spinnerService.show();
    this.userService.downloadPayoutReport(report.key).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (response && response.success && response.data && response.data.downloadUrl) {
          // Open download URL in new tab
          window.open(response.data.downloadUrl, '_blank');
          this.notifSer.success("Success", "Download started", {
            timeOut: 2000,
            showProgressBar: true,
            pauseOnHover: false,
            clickToClose: true,
            maxLength: 50,
          });
        } else {
          this.notifSer.error("Error", "Failed to generate download URL", {
            timeOut: 3000,
            showProgressBar: true,
            pauseOnHover: false,
            clickToClose: true,
            maxLength: 50,
          });
        }
      },
      (error) => {
        this.spinnerService.hide();
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to download report. Please try again.", {
          timeOut: 3000,
          showProgressBar: true,
          pauseOnHover: false,
          clickToClose: true,
          maxLength: 50,
        });
      }
    );
  }

  refreshReports() {
    this.loadReports();
  }
}



// WEBPACK FOOTER //
// ./src/app/s3-reports/s3-reports.component.ts